import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import fileService, { FileInfo } from '../services/fileService';
import expenseService from '../services/expenseService';
import { ExpenseResponseDTO } from '../types/expense.types';
import BeforeAfterComposer from '../components/BeforeAfterComposer';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import ExpenseForm from '../components/expenses/ExpenseForm';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  useTheme,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  ArrowBackOutlined,
  EditOutlined,
  LocationOnOutlined,
  PersonOutlined,
  DescriptionOutlined,
  ReceiptLongOutlined,
  PhotoLibraryOutlined,
  AddOutlined,
  AccountBalanceWalletOutlined,
  WorkOutlineOutlined,
  CalendarTodayOutlined,
  TrendingUpOutlined,
  CheckCircleOutline,
  CloseOutlined,
  ChevronLeftOutlined,
  ChevronRightOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  CompareOutlined,
} from '@mui/icons-material';
import { useProjetStore } from '../stores/useProjetStore';
import { useClientStore } from '../stores/useClientStore';
import { useQuoteStore } from '../stores/useQuoteStore';
import { Projet, StatutProjet, Devis, StatutDevis } from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`projet-tabpanel-${index}`}
      aria-labelledby={`projet-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ProjetDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { projets, loadProjets } = useProjetStore();
  const { clients, loadClients } = useClientStore();
  const { devis, loadDevis } = useQuoteStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projet, setProjet] = useState<Projet | null>(null);
  const [projetDevis, setProjetDevis] = useState<Devis[]>([]);
  const [projetPhotos, setProjetPhotos] = useState<FileInfo[]>([]);
  const [projetExpenses, setProjetExpenses] = useState<ExpenseResponseDTO[]>([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Upload state
  const [uploading, setUploading] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<FileInfo | null>(null);

  // Before/After dialog state
  const [beforeAfterDialogOpen, setBeforeAfterDialogOpen] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [selectedPhotoForComposer, setSelectedPhotoForComposer] = useState<FileInfo | null>(null);
  const [photoPreviews, setPhotoPreviews] = useState<Map<number, string>>(new Map());
  const [compositePhotos, setCompositePhotos] = useState<FileInfo[]>([]);

  // Expense dialog state
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);

  // Charger les données et trouver le projet
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Charger les données si nécessaire
        if (projets.length === 0) await loadProjets();
        if (clients.length === 0) await loadClients();
        if (devis.length === 0) await loadDevis();
      } catch (err) {
        console.error('Erreur lors du chargement des données:', err);
        setError('Erreur lors du chargement des données');
        setLoading(false);
      }
    };

    loadData();
  }, [loadProjets, loadClients, loadDevis, projets.length, clients.length, devis.length]);

  // Mettre à jour les données du projet quand les stores changent
  useEffect(() => {
    if (projets.length === 0) return;

    const foundProjet = projets.find((p) => p.id === Number(id));
    if (!foundProjet) {
      setError('Projet non trouvé');
      setProjet(null);
      setLoading(false);
      return;
    }

    setProjet(foundProjet);
    setError(null);

    // Filtrer les devis du projet
    const projetDevisList = devis.filter((d) => d.projetId === Number(id));
    setProjetDevis(projetDevisList);

    setLoading(false);
  }, [id, projets, devis]);

  // Charger les photos du projet (including composites)
  const loadPhotos = async () => {
    if (!id) return;
    setPhotosLoading(true);
    try {
      const allFiles = await fileService.getFilesByEntity('PHOTO_CHANTIER', Number(id));
      // Include all photos (regular photos AND composites)
      setProjetPhotos(allFiles);
    } catch (error) {
      console.error('Erreur lors du chargement des photos:', error);
    } finally {
      setPhotosLoading(false);
    }
  };

  // Charger les dépenses du projet
  const loadExpenses = async () => {
    if (!id) return;
    setExpensesLoading(true);
    try {
      const expenses = await expenseService.getByProject(Number(id));
      setProjetExpenses(expenses);
    } catch (error) {
      console.error('Erreur lors du chargement des dépenses:', error);
    } finally {
      setExpensesLoading(false);
    }
  };

  // Charger photos et dépenses quand on change d'onglet
  useEffect(() => {
    if (tabValue === 1) {
      loadPhotos();
    } else if (tabValue === 2) {
      loadExpenses();
    }
  }, [tabValue, id]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getClient = () => {
    if (!projet) return null;
    return clients.find((c) => c.id === projet.clientId);
  };

  const getStatutProjetColor = (statut: StatutProjet): string => {
    switch (statut) {
      case StatutProjet.EN_ATTENTE_DEVIS:
        return '#94A3B8';
      case StatutProjet.DEVIS_ENVOYE:
        return '#3B82F6';
      case StatutProjet.DEVIS_ACCEPTE:
        return '#10B981';
      case StatutProjet.EN_COURS:
        return '#F59E0B';
      case StatutProjet.TERMINE:
        return '#059669';
      case StatutProjet.ANNULE:
        return '#EF4444';
      default:
        return '#94A3B8';
    }
  };

  const getStatutProjetBgColor = (statut: StatutProjet): string => {
    switch (statut) {
      case StatutProjet.EN_ATTENTE_DEVIS:
        return '#F1F5F9';
      case StatutProjet.DEVIS_ENVOYE:
        return '#EFF6FF';
      case StatutProjet.DEVIS_ACCEPTE:
        return '#ECFDF5';
      case StatutProjet.EN_COURS:
        return '#FEF3C7';
      case StatutProjet.TERMINE:
        return '#D1FAE5';
      case StatutProjet.ANNULE:
        return '#FEE2E2';
      default:
        return '#F1F5F9';
    }
  };

  const getStatutProjetLabel = (statut: StatutProjet) => {
    switch (statut) {
      case StatutProjet.EN_ATTENTE_DEVIS:
        return 'En attente devis';
      case StatutProjet.DEVIS_ENVOYE:
        return 'Devis envoyé';
      case StatutProjet.DEVIS_ACCEPTE:
        return 'Devis accepté';
      case StatutProjet.EN_COURS:
        return 'En cours';
      case StatutProjet.TERMINE:
        return 'Terminé';
      case StatutProjet.ANNULE:
        return 'Annulé';
      default:
        return statut;
    }
  };

  const getStatutDevisColor = (statut: StatutDevis) => {
    switch (statut) {
      case StatutDevis.ACCEPTE:
        return 'success';
      case StatutDevis.ENVOYE:
        return 'info';
      case StatutDevis.REFUSE:
        return 'error';
      case StatutDevis.BROUILLON:
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatutDevisLabel = (statut: StatutDevis) => {
    switch (statut) {
      case StatutDevis.ACCEPTE:
        return 'Accepté';
      case StatutDevis.ENVOYE:
        return 'Envoyé';
      case StatutDevis.REFUSE:
        return 'Refusé';
      case StatutDevis.BROUILLON:
        return 'Brouillon';
      default:
        return statut;
    }
  };

  // Statistiques
  const stats = {
    totalDevis: projetDevis.length,
    devisAcceptes: projetDevis.filter((d) => d.statut === StatutDevis.ACCEPTE).length,
    devisEnvoyes: projetDevis.filter((d) => d.statut === StatutDevis.ENVOYE).length,
    montantTotalDevis: projetDevis.reduce((sum, d) => sum + (d.totalTTC || 0), 0),
    totalPhotos: projetPhotos.length,
    totalDepenses: projetExpenses.reduce((sum, e) => sum + e.amountTtc, 0),
  };

  // Fonction pour récupérer une URL présignée et afficher l'image
  const [imageUrls, setImageUrls] = useState<{ [key: number]: string }>({});

  const loadImageUrl = async (fileId: number) => {
    try {
      const { url } = await fileService.getPresignedUrl(fileId);
      setImageUrls((prev) => ({ ...prev, [fileId]: url }));
    } catch (error) {
      console.error('Erreur lors du chargement de l\'image:', error);
    }
  };

  useEffect(() => {
    projetPhotos.forEach((photo) => {
      if (!imageUrls[photo.id]) {
        loadImageUrl(photo.id);
      }
    });
  }, [projetPhotos]);

  // Load composite image URLs when composites are loaded
  useEffect(() => {
    compositePhotos.forEach((composite) => {
      if (!imageUrls[composite.id]) {
        loadImageUrl(composite.id);
      }
    });
  }, [compositePhotos]);

  // Handle photo upload
  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !id) return;

    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        await fileService.uploadFile(file, 'PHOTO_CHANTIER', Number(id));
      }
      // Reload photos after upload
      await loadPhotos();
    } catch (error) {
      console.error('Erreur lors de l\'upload des photos:', error);
      alert('Erreur lors de l\'upload des photos');
    } finally {
      setUploading(false);
      // Reset input
      event.target.value = '';
    }
  };

  // Handle lightbox navigation
  const handlePreviousPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev === 0 ? projetPhotos.length - 1 : prev - 1));
  };

  const handleNextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev === projetPhotos.length - 1 ? 0 : prev + 1));
  };

  const handleOpenLightbox = (index: number) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  };

  // Handle photo deletion
  const handleDeletePhoto = (photo: FileInfo) => {
    setPhotoToDelete(photo);
    setDeleteDialogOpen(true);
  };

  const confirmDeletePhoto = async () => {
    if (!photoToDelete) return;

    try {
      await fileService.deleteFile(photoToDelete.id);
      // Reload photos after deletion
      await loadPhotos();
      setDeleteDialogOpen(false);
      setPhotoToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression de la photo:', error);
      alert('Erreur lors de la suppression de la photo');
    }
  };

  const cancelDeletePhoto = () => {
    setDeleteDialogOpen(false);
    setPhotoToDelete(null);
  };

  // Load composite photos (before/after images)
  const loadComposites = async () => {
    if (!id) return;
    try {
      const allFiles = await fileService.getFilesByEntity('PHOTO_CHANTIER', Number(id));
      const composites = allFiles.filter((file) => file.isComposite);
      setCompositePhotos(composites);
    } catch (error) {
      console.error('Erreur lors du chargement des composites:', error);
    }
  };

  // Load photo previews for BeforeAfterComposer
  const loadPhotoPreviews = async () => {
    const previews = new Map<number, string>();
    for (const photo of projetPhotos) {
      try {
        const { url } = await fileService.getPresignedUrl(photo.id);
        previews.set(photo.id, url);
      } catch (error) {
        console.error(`Erreur lors du chargement de la preview pour ${photo.id}:`, error);
      }
    }
    setPhotoPreviews(previews);
  };

  // Handle opening before/after dialog
  const handleOpenBeforeAfterDialog = async () => {
    setBeforeAfterDialogOpen(true);
    await loadComposites();
    await loadPhotoPreviews();
  };

  // Handle creating new composite
  const handleCreateComposite = (photo: FileInfo) => {
    setSelectedPhotoForComposer(photo);
    setComposerOpen(true);
  };

  // Handle composite created
  const handleCompositeCreated = async (composite: FileInfo) => {
    await loadComposites();
    setComposerOpen(false);
    setSelectedPhotoForComposer(null);
  };

  // Handle delete composite
  const handleDeleteComposite = async (composite: FileInfo) => {
    if (!window.confirm(`Supprimer la composition "${composite.nom}" ?`)) return;
    try {
      await fileService.deleteFile(composite.id);
      await loadComposites();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression de la composition');
    }
  };

  // Handle expense created
  const handleExpenseCreated = async (expense: ExpenseResponseDTO) => {
    await loadExpenses();
    setExpenseDialogOpen(false);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (!loading && (error || !projet)) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Projet non trouvé'}
        </Alert>
        <Button variant="outlined" startIcon={<ArrowBackOutlined />} onClick={() => navigate('/projets')}>
          Retour à la liste
        </Button>
      </Container>
    );
  }

  if (!projet) {
    return null;
  }

  const client = getClient();

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header avec design amélioré */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 3,
          background: `linear-gradient(135deg, ${getStatutProjetBgColor(projet.statut)} 0%, white 100%)`,
          border: '2px solid',
          borderColor: getStatutProjetColor(projet.statut),
          borderRadius: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}>
            <IconButton
              onClick={() => navigate('/projets')}
              sx={{
                bgcolor: 'white',
                boxShadow: 1,
                '&:hover': { bgcolor: '#F9FAFB' },
              }}
            >
              <ArrowBackOutlined />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <WorkOutlineOutlined sx={{ fontSize: 32, color: getStatutProjetColor(projet.statut) }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1F2937' }}>
                  {projet.titre}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={getStatutProjetLabel(projet.statut)}
                  sx={{
                    bgcolor: getStatutProjetColor(projet.statut),
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                  }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarTodayOutlined sx={{ fontSize: 16 }} />
                  Créé le {new Date(projet.dateCreation).toLocaleDateString('fr-FR')}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<EditOutlined />}
            onClick={() => navigate(`/projets/edit/${projet.id}`)}
            sx={{
              bgcolor: getStatutProjetColor(projet.statut),
              '&:hover': { bgcolor: getStatutProjetColor(projet.statut), opacity: 0.9 },
            }}
          >
            Modifier
          </Button>
        </Box>
      </Paper>

      {/* Informations du projet - Design amélioré */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Client */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={2}
            sx={{
              height: '100%',
              cursor: client ? 'pointer' : 'default',
              transition: 'all 0.2s',
              '&:hover': client ? { transform: 'translateY(-4px)', boxShadow: 4 } : {},
              borderLeft: '4px solid #3B82F6',
            }}
            onClick={() => client && navigate(`/clients/${client.id}`)}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: '#EFF6FF',
                    borderRadius: 2,
                  }}
                >
                  <PersonOutlined sx={{ fontSize: 28, color: '#3B82F6' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Client
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937', mt: 0.5 }}>
                    {client ? `${client.prenom} ${client.nom}` : 'Client inconnu'}
                  </Typography>
                  {client && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {client.email}
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Adresse */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={2}
            sx={{
              height: '100%',
              borderLeft: '4px solid #10B981',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: '#ECFDF5',
                    borderRadius: 2,
                  }}
                >
                  <LocationOnOutlined sx={{ fontSize: 28, color: '#10B981' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Adresse du chantier
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937', mt: 0.5 }}>
                    {projet.adresseChantier}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Description */}
        {projet.description && (
          <Grid item xs={12} md={6}>
            <Card
              elevation={2}
              sx={{
                height: '100%',
                borderLeft: '4px solid #6366F1',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: '#EEF2FF',
                      borderRadius: 2,
                    }}
                  >
                    <DescriptionOutlined sx={{ fontSize: 28, color: '#6366F1' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      Description
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#4B5563', mt: 0.5, lineHeight: 1.6 }}>
                      {projet.description}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Période */}
        {(projet.dateDebut || projet.dateFin) && (
          <Grid item xs={12} md={projet.description ? 6 : 12}>
            <Card
              elevation={2}
              sx={{
                height: '100%',
                borderLeft: '4px solid #F59E0B',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: '#FEF3C7',
                      borderRadius: 2,
                    }}
                  >
                    <CalendarTodayOutlined sx={{ fontSize: 28, color: '#F59E0B' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      Période du projet
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937', mt: 0.5 }}>
                      {projet.dateDebut && new Date(projet.dateDebut).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                      {projet.dateDebut && projet.dateFin && ' → '}
                      {projet.dateFin && new Date(projet.dateFin).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Statistiques - Design épuré et professionnel */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={1}
            sx={{
              bgcolor: 'white',
              borderLeft: '4px solid #6366F1',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3,
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: '#EEF2FF',
                    borderRadius: 2,
                  }}
                >
                  <DescriptionOutlined sx={{ fontSize: 28, color: '#6366F1' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Total Devis
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#1F2937', my: 0.5 }}>
                    {stats.totalDevis}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckCircleOutline sx={{ fontSize: 16, color: '#10B981' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#10B981' }}>
                      {stats.devisAcceptes} acceptés
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={1}
            sx={{
              bgcolor: 'white',
              borderLeft: '4px solid #F59E0B',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3,
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: '#FEF3C7',
                    borderRadius: 2,
                  }}
                >
                  <TrendingUpOutlined sx={{ fontSize: 28, color: '#F59E0B' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Montant Total
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1F2937', mt: 0.5 }}>
                    {stats.montantTotalDevis.toFixed(2)} €
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={1}
            sx={{
              bgcolor: 'white',
              borderLeft: '4px solid #3B82F6',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3,
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: '#EFF6FF',
                    borderRadius: 2,
                  }}
                >
                  <PhotoLibraryOutlined sx={{ fontSize: 28, color: '#3B82F6' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Photos
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#1F2937', mt: 0.5 }}>
                    {stats.totalPhotos}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={1}
            sx={{
              bgcolor: 'white',
              borderLeft: '4px solid #10B981',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3,
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: '#ECFDF5',
                    borderRadius: 2,
                  }}
                >
                  <AccountBalanceWalletOutlined sx={{ fontSize: 28, color: '#10B981' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Dépenses
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1F2937', mt: 0.5 }}>
                    {stats.totalDepenses.toFixed(2)} €
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs pour Devis, Photos et Dépenses */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="projet tabs">
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionOutlined />
                  <span>Devis ({stats.totalDevis})</span>
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhotoLibraryOutlined />
                  <span>Photos</span>
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountBalanceWalletOutlined />
                  <span>Dépenses</span>
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* Tab Devis */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddOutlined />}
              onClick={() => navigate(`/devis/new?projetId=${projet.id}`)}
            >
              Nouveau devis
            </Button>
          </Box>
          {projetDevis.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <DescriptionOutlined sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Aucun devis pour ce projet
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Numéro</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date d'émission</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Montant TTC
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projetDevis
                    .sort((a, b) => new Date(b.dateEmission).getTime() - new Date(a.dateEmission).getTime())
                    .map((devisItem) => (
                      <TableRow
                        key={devisItem.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/devis/${devisItem.id}`)}
                      >
                        <TableCell>{devisItem.numero}</TableCell>
                        <TableCell>{new Date(devisItem.dateEmission).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell>
                          <Chip
                            label={getStatutDevisLabel(devisItem.statut)}
                            color={getStatutDevisColor(devisItem.statut)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {devisItem.totalTTC.toFixed(2)} €
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/devis/${devisItem.id}`);
                            }}
                          >
                            Voir
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Tab Photos */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<CompareOutlined />}
              onClick={handleOpenBeforeAfterDialog}
            >
              Gérer avant/après
            </Button>
            <Button
              variant="contained"
              component="label"
              startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadOutlined />}
              disabled={uploading}
            >
              {uploading ? 'Upload en cours...' : 'Ajouter des photos'}
              <input type="file" hidden accept="image/*" multiple onChange={handlePhotoUpload} />
            </Button>
          </Box>
          {photosLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : projetPhotos.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PhotoLibraryOutlined sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Aucune photo pour ce projet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Cliquez sur "Ajouter des photos" pour commencer.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {projetPhotos.map((photo, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={photo.id}>
                  <Card
                    elevation={2}
                    sx={{
                      position: 'relative',
                      transition: 'transform 0.2s',
                      border: photo.isComposite ? '2px solid #F59E0B' : 'none',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: 4,
                      },
                      '&:hover .delete-button': {
                        opacity: 1,
                      },
                    }}
                  >
                    {/* Composite Badge */}
                    {photo.isComposite && (
                      <Chip
                        label="AVANT/APRÈS"
                        size="small"
                        icon={<CompareOutlined />}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          bgcolor: '#F59E0B',
                          color: 'white',
                          fontWeight: 600,
                          zIndex: 2,
                          fontSize: '0.7rem',
                        }}
                      />
                    )}

                    {/* Delete button */}
                    <IconButton
                      className="delete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePhoto(photo);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        zIndex: 2,
                        '&:hover': {
                          bgcolor: 'rgba(239, 68, 68, 0.9)',
                        },
                      }}
                    >
                      <DeleteOutlined />
                    </IconButton>

                    <Box
                      sx={{ cursor: 'pointer' }}
                      onClick={() => imageUrls[photo.id] && handleOpenLightbox(index)}
                    >
                      {imageUrls[photo.id] ? (
                        <Box
                          component="img"
                          src={imageUrls[photo.id]}
                          alt={photo.nom}
                          sx={{
                            width: '100%',
                            height: 200,
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: 200,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#F3F4F6',
                          }}
                        >
                          <CircularProgress size={30} />
                        </Box>
                      )}
                    </Box>

                    <CardContent>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {photo.nom}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(photo.dateUpload).toLocaleDateString('fr-FR')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {fileService.formatFileSize(photo.tailleFichier)}
                      </Typography>
                      {photo.isComposite && photo.compositeMetadata?.zone && (
                        <Typography variant="caption" color="primary" sx={{ display: 'block', fontWeight: 600 }}>
                          📍 {photo.compositeMetadata.zone}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        {/* Tab Dépenses */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddOutlined />}
              onClick={() => setExpenseDialogOpen(true)}
            >
              Nouvelle dépense
            </Button>
          </Box>
          {expensesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : projetExpenses.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <AccountBalanceWalletOutlined sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Aucune dépense pour ce projet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Cliquez sur "Nouvelle dépense" pour ajouter une dépense.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Libellé</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Catégorie</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Montant HT
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      TVA
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Montant TTC
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projetExpenses
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                    .map((expense) => (
                      <TableRow key={expense.id} hover>
                        <TableCell>{new Date(expense.date).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell>{expense.label}</TableCell>
                        <TableCell>
                          <Chip label={expense.category} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">{expense.amountHt.toFixed(2)} €</TableCell>
                        <TableCell align="right">{expense.amountTva.toFixed(2)} €</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {expense.amountTtc.toFixed(2)} €
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={expense.status === 'VALIDATED' ? 'Validée' : 'En attente'}
                            color={expense.status === 'VALIDATED' ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  <TableRow>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                      Total:
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#10B981' }}>
                      {stats.totalDepenses.toFixed(2)} €
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Paper>

      {/* Photo Lightbox Dialog */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.95)',
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Close button */}
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
              zIndex: 10,
            }}
          >
            <CloseOutlined />
          </IconButton>

          {/* Previous button */}
          {projetPhotos.length > 1 && (
            <IconButton
              onClick={handlePreviousPhoto}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                zIndex: 10,
              }}
            >
              <ChevronLeftOutlined sx={{ fontSize: 40 }} />
            </IconButton>
          )}

          {/* Next button */}
          {projetPhotos.length > 1 && (
            <IconButton
              onClick={handleNextPhoto}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                zIndex: 10,
              }}
            >
              <ChevronRightOutlined sx={{ fontSize: 40 }} />
            </IconButton>
          )}

          {/* Current photo */}
          {projetPhotos[currentPhotoIndex] && imageUrls[projetPhotos[currentPhotoIndex].id] && (
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Box
                component="img"
                src={imageUrls[projetPhotos[currentPhotoIndex].id]}
                alt={projetPhotos[currentPhotoIndex].nom}
                sx={{
                  maxWidth: '90%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  margin: '0 auto',
                }}
              />
              <Typography variant="body1" sx={{ color: 'white', mt: 2, fontWeight: 600 }}>
                {projetPhotos[currentPhotoIndex].nom}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {currentPhotoIndex + 1} / {projetPhotos.length}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDeletePhoto}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer la photo "{photoToDelete?.nom}" ?
            Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeletePhoto} color="inherit">
            Annuler
          </Button>
          <Button onClick={confirmDeletePhoto} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Before/After Management Dialog */}
      <Dialog
        open={beforeAfterDialogOpen}
        onClose={() => setBeforeAfterDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '600px' },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Gestion des photos avant/après</Typography>
            <Button onClick={() => setBeforeAfterDialogOpen(false)} startIcon={<CloseOutlined />} size="small">
              Fermer
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Existing Composites */}
          {compositePhotos.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CompareOutlined />
                Compositions existantes ({compositePhotos.length})
              </Typography>
              <Grid container spacing={2}>
                {compositePhotos.map((composite) => (
                  <Grid item xs={12} md={6} key={composite.id}>
                    <Card elevation={2} sx={{ position: 'relative' }}>
                      <IconButton
                        onClick={() => handleDeleteComposite(composite)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'rgba(0, 0, 0, 0.6)',
                          color: 'white',
                          zIndex: 2,
                          '&:hover': {
                            bgcolor: 'rgba(239, 68, 68, 0.9)',
                          },
                        }}
                      >
                        <DeleteOutlined />
                      </IconButton>
                      {imageUrls[composite.id] ? (
                        <Box
                          component="img"
                          src={imageUrls[composite.id]}
                          alt={composite.nom}
                          sx={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: 400,
                            objectFit: 'contain',
                            bgcolor: '#000',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: 300,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#F3F4F6',
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      )}
                      <CardContent>
                        <Typography variant="body2" fontWeight={600}>
                          {composite.nom}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(composite.dateUpload).toLocaleDateString('fr-FR')}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Divider sx={{ my: 3 }} />
            </Box>
          )}

          {/* Create New Composite */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddOutlined />
              Créer une nouvelle composition
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Sélectionnez une photo "APRÈS" pour créer une composition avant/après
            </Typography>

            {projetPhotos.filter((p) => !p.isComposite).length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                Aucune photo disponible. Veuillez d'abord uploader des photos dans l'onglet Photos.
              </Alert>
            ) : (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {projetPhotos
                  .filter((photo) => !photo.isComposite)
                  .map((photo) => (
                    <Grid item xs={6} sm={4} md={3} key={photo.id}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: 3,
                          },
                        }}
                        onClick={() => handleCreateComposite(photo)}
                      >
                        {imageUrls[photo.id] ? (
                          <Box
                            component="img"
                            src={imageUrls[photo.id]}
                            alt={photo.nom}
                            sx={{
                              width: '100%',
                              height: 150,
                              objectFit: 'cover',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: 150,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: '#F3F4F6',
                            }}
                          >
                            <CircularProgress size={30} />
                          </Box>
                        )}
                        <CardContent sx={{ py: 1 }}>
                          <Typography variant="caption" noWrap>
                            {photo.nom}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* BeforeAfterComposer Dialog */}
      {selectedPhotoForComposer && (
        <BeforeAfterComposer
          open={composerOpen}
          onClose={() => {
            setComposerOpen(false);
            setSelectedPhotoForComposer(null);
          }}
          photoApres={selectedPhotoForComposer}
          photoApresPreview={imageUrls[selectedPhotoForComposer.id] || ''}
          availablePhotos={projetPhotos.filter((p) => p.id !== selectedPhotoForComposer.id && !p.isComposite)}
          photoPreviews={photoPreviews}
          projetId={Number(id)}
          onCompositeCreated={handleCompositeCreated}
        />
      )}

      {/* Expense Creation Dialog */}
      <Dialog
        open={expenseDialogOpen}
        onClose={() => setExpenseDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '500px' },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Nouvelle dépense pour {projet.titre}</Typography>
            <IconButton onClick={() => setExpenseDialogOpen(false)} size="small">
              <CloseOutlined />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <ExpenseForm
            onSuccess={handleExpenseCreated}
            onCancel={() => setExpenseDialogOpen(false)}
            projects={projets}
            initialData={{ projetId: projet.id } as any}
            mode="create"
          />
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ProjetDetailPage;
